// Shared TypeScript types for epics domain
// Used by both backend and web layers

// M3 (2026-07-14): extended to the full cross-repo EPIC_STATES set (Forge
// src/orchestrator/cross-repo.ts). This was a stale subset — cross-repo epics
// routinely sit in states the union didn't name (COMPLETED, the FORGE-306/313
// gates, EPIC_VALIDATING, rollback). Keep in sync with that source of truth.
export type EpicState =
  | 'PLAN_POSTED'
  | 'IN_PROGRESS'
  | 'COMPLETE'
  | 'COMPLETED'
  | 'FAILED'
  | 'ABANDONED'
  | 'DEFINED'
  | 'BUILDING'
  // FORGE-306/313 gates
  | 'GRAPH_REFRESHING'
  | 'REQUIREMENTS_REVIEW'
  | 'REQUIREMENTS_ALIGNED'
  | 'REQUIREMENTS_QUESTIONS'
  | 'REQUIREMENTS_QUESTIONS_IN_PROGRESS'
  | 'REQUIREMENTS_REPLY_RECEIVED'
  | 'TARGETS_PENDING'
  | 'PLANNING'
  | 'PLAN_QUESTIONS'
  | 'PLAN_QUESTIONS_IN_PROGRESS'
  | 'EPIC_VALIDATING'
  | 'READY_FOR_DEPLOY'
  | 'DEPLOYING_DEV'
  | 'TESTING_DEV'
  | 'DEPLOYING_TEST'
  | 'TESTING_TEST'
  | 'ROLLBACK_IN_PROGRESS'
  | 'ROLLED_BACK'
  | 'READY_FOR_REVIEW'
  | 'READY_FOR_REVIEW_NOTIFIED'
  // FORGE-039 bug fix states. Structurally identical to an epic — just a
  // different state machine + branch prefix on the orchestrator side.
  | 'BUGFIX_DEFINED'
  | 'BUGFIX_IN_PROGRESS'
  | 'BUGFIX_COMPLETE'
  | 'ABORTED';

/** Discriminator on the META row. Epics run a full plan; bug fixes are
 *  a single-story shortcut path triggered by the `agent:bug` label. 'init' is
 *  the brownfield deep-init pseudo-epic (REPO#<org>/<repo>#EPIC#0, states
 *  INIT_DEFINED..INIT_COMPLETE/INIT_FAILED) — not user-creatable (absent from
 *  CreateEpicKind below), but a real value Forge writes and the epic list
 *  needs to render sensibly rather than treat as a malformed 'epic' row. */
export type EpicKind = 'epic' | 'bugfix' | 'init';

/**
 * FORGE-276 — an orthogonal `epic_kind` discriminator the orchestrator stamps
 * on the META row for specialised epic flows (currently 'migration' for the
 * legacy .NET migration path, and 'safety-net'). Distinct from `kind` (which
 * only ever discriminates 'epic' vs 'bugfix'); absent on ordinary epics.
 */
export type EpicKindTag = 'migration' | 'safety-net';

/**
 * FORGE-276 — per-project migratability the migration-analyst assigns each
 * migration story. 'mechanical' = tooling can auto-convert; 'assisted' = an
 * agent can convert with guidance; 'human-required' = a hard blocker
 * (WCF/WebForms) a human must resolve.
 */
export type MigrationClassification = 'mechanical' | 'assisted' | 'human-required';

/**
 * Two storage models for an epic (see DDB reference):
 *  - 'single-repo' : PK = REPO#<org>/<repo>#EPIC#<n>, SK = META
 *  - 'cross-repo'  : PK = PROJECT#<id>#EPIC#<n>,      SK = META  (v2, epic-as-partition)
 */
export type EpicModel = 'single-repo' | 'cross-repo';

export type AgentSessionOutcome = 'COMPLETED' | 'FAILED' | 'PARTIAL' | 'CANCELLED';

export interface GitHubInfo {
  issueUrl: string;
  approvedBy: string | null;
  approvedAt: string | null;
}

export interface EpicDynamoItem {
  PK: string;
  SK: string;
  epic_id?: string;
  number?: number;
  title?: string;
  repo?: string;
  github_org?: string;
  github_repo?: string;
  state?: string;
  created_at?: string;
  updated_at?: string;
  story_count?: number;
  completed_story_count?: number;
  github_issue_url?: string;
  approved_by?: string;
  approved_at?: string;
  github_issue_id?: number;
  /** 'epic' (default when absent — pre-FORGE-039 rows) or 'bugfix'. */
  kind?: EpicKind;
  /** FORGE-276 — specialised epic flow discriminator ('migration' | 'safety-net');
   *  absent on ordinary epics. Orthogonal to `kind`. */
  epic_kind?: EpicKindTag | string;
  /** Branch the orchestrator opened for the work — `epic/<n>` or `bugfix/<n>-<slug>`. */
  branch?: string;
  /** PR URL once the work hits READY_FOR_REVIEW_NOTIFIED. */
  pr_url?: string;
  /** Rollup of every SESSION cost for this epic/bugfix. Forge writes
   *  this on the META row so we don't need to re-sum on every list call. */
  total_cost_usd?: number;
  // ── FORGE-055 story-point + cost-efficiency fields (META row) ──
  /** Sum of STORY# story_points. Written at BUILDING time, so present
   *  while the epic is in flight (unlike the cost fields below). */
  total_story_points?: number;
  /** Set only at epic completion by buildEpicCostSummary. */
  epic_total_cost_usd?: number;
  /** Set only at epic completion. Usually equals total_story_points but
   *  Forge writes it separately so we never have to recompute. */
  epic_total_story_points?: number;
  /** epic_total_cost_usd / epic_total_story_points. Present only when
   *  the epic has completed — the canonical "epic is done" signal. */
  cost_per_story_point?: number;
  /** ISO timestamp the epic completed. Falls back to updated_at when absent. */
  completed_at?: string;
  // ── Cross-repo epic fields (PROJECT#<id> / EPIC#<n>) ──
  /** Repo where operator comments are posted, e.g. "org/repo". */
  anchor_repo?: string;
  /** GitHub issue on the anchor repo that serves as the epic's command thread. */
  primary_issue?: { repo: string; number: number };
  /** Every repo in scope for a cross-repo epic. */
  involved_repos?: string[];
  /** FORGE-207: resolved-config snapshot written by the epic-planner at boot
   *  (impl agents write theirs on the STORY rows). */
  resolved_config?: ResolvedConfigDynamo;
  // ── FORGE-269/FORGE-303 cost quote + calibration fields (META row) ──
  cost_quote_mid_usd?: number;
  cost_quote_low_usd?: number;
  cost_quote_high_usd?: number;
  cost_quote_per_point_usd?: number;
  /** FORGE-350: which flow the stored quote prices ('parity' = Parity Proof; absent = the epic plan). */
  cost_quote_kind?: 'parity' | 'plan';
  /** Parity Proof run start (ISO) — the window the run's actual cost is summed over (FORGE-397). */
  parity_at?: string;
  cost_quote_points?: number;
  cost_quote_basis?: string;
  cost_quote_basis_kind?: string;
  cost_quote_posted_at?: string;
  /** FORGE-303 — bottom-up, pre-calibration base; the learnable anchor. */
  cost_estimate_base_usd?: number;
  cost_calibration_factor?: number;
  cost_calibration_n?: number;
  cost_band_source?: 'project-calibrated' | 'fleet-fallback';
  cost_estimate_dominant_confidence?: 'high' | 'medium' | 'low';
  /** Completion delta (FORGE-269), reused by FORGE-303's estimate-vs-actual. */
  quote_actual_delta_usd?: number;
  quote_actual_delta_pct?: number;
  quote_within_band?: boolean;
}

export interface EpicSummary {
  id: number;
  number: number;
  title: string;
  repo: string;
  state: string;
  createdAt: string;
  updatedAt: string;
  storyCount: number;
  completedStoryCount: number;
  github: GitHubInfo;
  kind: EpicKind;
  /** FORGE-276 — specialised epic flow ('migration' | 'safety-net'); null on
   *  ordinary epics. Orthogonal to `kind`. */
  epicKind?: string | null;
  branch: string | null;
  prUrl: string | null;
  totalCostUsd: number;
  /**
   * Opaque, URL-safe handle to this epic's DDB identity (encodes PK|SK).
   * Use it to build the detail link — works for both single-repo and
   * cross-repo epics. See {@link encodeEpicRef}.
   */
  epicRef: string;
  /** Which storage model this epic uses. */
  model: EpicModel;
  /** For cross-repo epics, the repos in scope; null for single-repo. */
  involvedRepos: string[] | null;
  /** FORGE-159: archive / auto-hide visibility (for the "Show archived" toggle + badges). */
  archive?: EpicArchiveInfo;
}

/** FORGE-159: archive visibility descriptor surfaced on each epic summary. */
export interface EpicArchiveInfo {
  /** Hidden from the default list (manual archive OR auto-hidden). */
  hidden: boolean;
  /** Operator explicitly archived it. */
  archived: boolean;
  /** Complete + older than the auto-hide window (no manual archive). */
  autoHidden: boolean;
  archiveReason: string | null;
  archivedAt: string | null;
  archivedBy: string | null;
}

export interface EpicDetail {
  id: number;
  number: number;
  title: string;
  repo: string;
  state: string;
  createdAt: string;
  updatedAt: string;
  storyCount: number;
  completedStoryCount: number;
  github: GitHubInfo;
  kind: EpicKind;
  /** FORGE-276 — specialised epic flow ('migration' | 'safety-net'); null on
   *  ordinary epics. Orthogonal to `kind`. */
  epicKind?: string | null;
  branch: string | null;
  prUrl: string | null;
  totalCostUsd: number;
  /** URL-safe handle encoding PK|SK — same as EpicSummary.epicRef. */
  epicRef: string;
  /** Storage model. */
  model: EpicModel;
  /** Cross-repo scope; null for single-repo. */
  involvedRepos: string[] | null;
  stories: StorySummary[];
  /**
   * Epic-level live agents (epic-planner / product-manager / solution-analyst)
   * currently running — derived from the EPIC META row's heartbeat maps. These
   * agents don't own a STORY row, so their in-flight runs are invisible until
   * they complete unless surfaced here. Only non-stale (genuinely active) runs.
   * Empty when nothing epic-level is running.
   */
  liveAgents?: LiveAgent[];
  /**
   * Every SESSION# row found under this epic's PK. The UI groups them
   * by `storyId` and renders one table per story. Sorted newest-first.
   */
  sessions: EpicSession[];
  // ── FORGE-055 story-point + cost-efficiency rollup (from META) ──
  /** Sum of story points. Available from BUILDING onwards; null on
   *  legacy (pre-FORGE-055) epics. */
  totalStoryPoints: number | null;
  /** Set only at epic completion; null while in progress / legacy. */
  epicTotalCostUsd: number | null;
  /** cost_per_story_point. null until the epic completes. */
  costPerStoryPoint: number | null;
  /** FORGE-154: "why is this stuck?" — per-story diagnosis + headline. */
  diagnosis?: EpicDiagnosis;
  /** FORGE-207: the plan/stack/limits this epic's agents actually ran with —
   *  snapshotted from the injected env at agent boot. null on epics whose runs
   *  predate FORGE-207 (no snapshot on any row). */
  resolvedConfig?: ResolvedEpicConfig | null;
  /** FORGE-302: PR review tracking snapshot; null when tracking was never
   *  enabled for this repo/plan or no PR has been opened yet. For a cross-repo
   *  epic this is the "primary" PR (first non-terminal) — kept for back-compat;
   *  prefer `prWatches` when rendering. */
  prWatch?: PRWatchInfo | null;
  /** FORGE-308: ALL tracked PRs for this epic — one per involved repo on a
   *  cross-repo epic, a single-element array on a single-repo epic. Empty when
   *  none are tracked. Each entry carries its `repo`. */
  prWatches?: PRWatchInfo[];
  /** FORGE-308: epic-level rollup over `prWatches`. Null when nothing is tracked. */
  prTailState?: PRTailState | null;
  /** FORGE-303: calibrated cost estimate posted at PLAN_POSTED, plus the
   *  actual-vs-quote delta once the epic completes. Null on epics predating
   *  FORGE-303 (no cost_estimate_base_usd on the row) or before a plan posts. */
  costEstimate?: CostEstimateInfo | null;
}

/**
 * FORGE-303 — calibrated cost estimation. `midUsd`/`lowUsd`/`highUsd` are what
 * was actually POSTED to the operator (either the project-calibrated quote or
 * the FORGE-269 fleet/repo-history fallback); `baseEstimateUsd` is always the
 * pre-calibration bottom-up number this project's next epic learns from.
 * `actualUsd`/`deltaUsd`/`deltaPct`/`withinBand` are null until the epic
 * completes (see FR-16 — "estimate-vs-actual and whether it landed in band").
 */
export interface CostEstimateInfo {
  midUsd: number;
  lowUsd: number;
  highUsd: number;
  perPointUsd: number;
  /** One-line human basis, e.g. "corrected ×1.28 from 6 prior epics on this project; ±22% band". */
  basis: string;
  bandSource: 'project-calibrated' | 'fleet-fallback';
  /** FORGE-350: 'parity' when this is the Parity Proof quote, else the epic plan's. */
  kind?: 'plan' | 'parity';
  /** Points the quote was priced on (Parity Proof sizing), when known. */
  points?: number | null;
  /** Bottom-up, pre-calibration base ($) — the number this project's calibration learns from. */
  baseEstimateUsd: number;
  /** Correction factor applied; 1 when `bandSource === 'fleet-fallback'`. */
  calibrationFactor: number;
  /** Sample size (prior completed epics on this project) behind `calibrationFactor`. */
  calibrationN: number;
  /** Most common per-story confidence grade across the plan (FR-15). */
  dominantConfidence: 'high' | 'medium' | 'low';
  postedAt: string | null;
  /** Populated once the epic completes; null while in flight. */
  actualUsd: number | null;
  deltaUsd: number | null;
  deltaPct: number | null;
  withinBand: boolean | null;
  /**
   * FORGE-397: Parity Proof only — what THIS run has cost so far (sessions since
   * `runSince`), the same window the estimate prices. The epic's Build cost is
   * the lifetime total (every run and phase), so comparing it with the estimate
   * was apples to oranges. Null for plan quotes or when the run start is unknown.
   */
  runActualUsd?: number | null;
  /** Part of runActualUsd spent on sessions that crashed / were cut off. */
  runReworkUsd?: number | null;
  runFailedRuns?: number | null;
  runSince?: string | null;
  runDeltaPct?: number | null;
  runWithinBand?: boolean | null;
}

/**
 * FORGE-207 — the resolved config a run actually used, snapshotted by the agent
 * at boot from the env the orchestrator injected. This is the accurate record
 * of what the engine ran with (not a portal re-derivation), so a stack/plan
 * mismatch — e.g. a dotnet/business project that ran on node/starter — is
 * visible at a glance.
 */
export interface ResolvedEpicConfig {
  plan: string | null;
  tier: string | null;
  stackBackend: string | null;
  stackWeb: string | null;
  stackMobile: string | null;
  stackWindows: string | null;
  stackWindowsService: string | null;
  stackDatabase: string | null;
  maxIterations: number | null;
  maxConcurrentStories: number | null;
  repoSkillsEnabled: boolean | null;
  mcpServerCount: number | null;
  /** When/which agent captured this snapshot. */
  snapshotAt: string | null;
  snapshotBy: string | null;
}

/**
 * FORGE-275 — characterization-test safety net. Per-module coverage the
 * qa-validate runner measured for the story. Keyed by module path in
 * `StorySummary.safetyNetModuleCoverage`.
 */
export interface ModuleCoverage {
  /** Line coverage ratio in [0, 1] (e.g. 0.72 → 72%). */
  coverage: number;
  linesCovered: number;
  linesTotal: number;
  /** Number of source files under this module. */
  files: number;
}

/**
 * FORGE-275 — a module whose coverage regressed against its baseline. Only
 * present on regression failures.
 */
export interface ModuleRegression {
  module: string;
  /** Baseline coverage ratio in [0, 1]. */
  baseline: number;
  /** Current coverage ratio in [0, 1]. */
  current: number;
  /** current - baseline (negative on a regression). */
  delta: number;
}

/** FORGE-093 retry-analyst brief, surfaced verbatim on a failed story. */
export interface RetryAdvice {
  verdict: string | null;
  diagnosis: string | null;
  rootCauseKind: string | null;
  fixSteps: string[];
  keep: string[];
  dontRepeat: string[];
  producedAt: string | null;
}

/**
 * FORGE-207 — per-story "why it stopped" derived from fields Forge already
 * writes on a force-complete / failure but never surfaced: the real exit reason
 * (no_progress vs max_iterations, un-masked by FORGE-203), the agent's last
 * unwritten diagnosis, the stall reason, and the retry-analyst brief.
 */
export interface StoryFailureAnalysis {
  /** no_progress | max_iterations | deadline | qa_budget | … (last attempt). */
  exitReason: string | null;
  /** The agent's last free-text reasoning captured at force-complete. */
  lastAttemptNotes: string | null;
  lastAttemptAt: string | null;
  /** Reason the story was paused/blocked (PAUSED_NEEDS_HUMAN etc.). */
  stallReason: string | null;
  /** Retry-analyst brief, when one ran for this story. */
  retryAdvice: RetryAdvice | null;
}

/** FORGE-154: per-story stuck diagnosis surfaced on the epic detail page. */
export interface StoryDiagnosis {
  kind:
    | 'merge_blocked' | 'needs_answer' | 'force_complete'
    | 'blocked' | 'testing_failed' | 'stalled' | 'awaiting_approval';
  severity: 'critical' | 'warning' | 'info';
  summary: string;
  detail?: string;
  suggestedCommand?: string;
  stuckMinutes: number;
  storyRef?: string;
}

export interface EpicDiagnosis {
  headline: string | null;
  stories: StoryDiagnosis[];
}

// ---------------------------------------------------------------------------
// FORGE-302 — PR review tracking & auto-response. Surfaced on the epic detail
// page (GET /api/v1/epics/:epicId) from the PRWATCH#<pr-number> row Forge
// writes alongside the epic's META row once a PR is opened with
// pr_tracking.enabled on. Absent (prWatch: null) when tracking was never
// enabled for this repo/plan, or the epic hasn't reached READY_FOR_REVIEW yet.
// ---------------------------------------------------------------------------

export type PRWatchState =
  | 'WATCHING'          // no pending feedback; idle, listening for webhooks
  | 'FEEDBACK_QUEUED'   // a batch was classified as actionable; pr-fix about to launch
  | 'FIXING'            // pr-fix agent is actively working a round
  | 'PROPOSING_RESOLUTION' // FORGE-309: pr-resolution-proposer drafting a recommendation
  | 'RESOLUTION_REVIEW'    // FORGE-309: pr-resolution-review gating the draft
  | 'RESOLUTION_READY'     // FORGE-309: reviewer approved — awaiting the human's Approve/Reject
  | 'PR_FIX_ESCALATED'  // round cap hit OR classifier asked for a human — auto-loop paused
  | 'PR_MERGED'         // terminal — tracking ended, PR merged
  | 'PR_CLOSED';        // terminal — tracking ended, PR closed without merging

/**
 * FORGE-308 — epic-level rollup over a cross-repo epic's N per-PR watch rows.
 *   ESCALATED  — at least one PR needs a human (fix-round cap hit).
 *   TRACKING   — at least one PR still non-terminal (work in flight).
 *   ALL_MERGED — every PR merged.
 *   PARTIAL    — all terminal, mixed merged/closed.
 */
export type PRTailState = 'TRACKING' | 'ALL_MERGED' | 'PARTIAL' | 'ESCALATED';

/** Per-PR tracking snapshot for the epic detail page (Forge's PRWATCH# row, mapped). */
export interface PRWatchInfo {
  prNumber: number;
  prUrl: string;
  prTitle: string | null;
  /** "org/repo" for a cross-repo epic's PR (FORGE-308); null for a single-repo epic. */
  repo: string | null;
  state: PRWatchState;
  /** Automated fix rounds attempted so far. */
  fixRound: number;
  /** Configured cap (`pr_tracking.max_fix_rounds`) — fixRound reaching this triggers PR_FIX_ESCALATED. */
  maxFixRounds: number;
  /** Whether this repo/plan has auto-fix on (vs. watch/notify-only). */
  autoFixEnabled: boolean;
  /** Feedback items queued but not yet dispatched (still inside the debounce window). */
  pendingFeedbackCount: number;
  lastActivityAt: string;
  lastDispatchAt: string | null;
  /** Cumulative Haiku classifier cost for this PR — does not include pr-fix agent session cost. */
  classifierCostUsd: number;
  /**
   * The classifier's reason for escalating to PR_FIX_ESCALATED (or the
   * round-cap message), persisted on the row itself — not just posted as a
   * GitHub comment, which can silently fail to post (wrong repo resolution,
   * rate limit, permissions). Null outside PR_FIX_ESCALATED, or for rows
   * written before this field existed.
   */
  escalationReason: string | null;
  /**
   * FORGE-309: which propose→review round produced the current proposal
   * (0-based). Only meaningful in the RESOLUTION_* states; 0 otherwise.
   */
  resolutionRound: number;
  /**
   * FORGE-309: the reviewed recommendation awaiting the human, populated in
   * RESOLUTION_READY (and, mid-loop, RESOLUTION_REVIEW). Null otherwise or for
   * rows written before this field existed. When `hasCodeChanges` is true the
   * proposal's commits live on `proposalBranch` (never the PR head); Approve
   * merges that branch onto the PR head.
   */
  proposedResolution: {
    summary: string;
    recommendation: string;
    hasCodeChanges: boolean;
    proposalBranch: string | null;
    reviewVerdict: 'approve' | 'changes_requested' | null;
    reviewNotes: string | null;
  } | null;
  /**
   * The PR-tracking agent runs for this PR (pr-fix / pr-resolution-proposer /
   * pr-resolution-review), newest first. These sessions have no STORY# row —
   * the epic detail route matches them to this PR by the PR number embedded in
   * their storyId (see prNumberFromSessionStoryId) so the UI can render them
   * under the PR card the way story agents render under a story.
   */
  sessions: EpicSession[];
}

/**
 * Parse the PR number a PR-tracking agent session belongs to, or null when the
 * storyId isn't a PR-tracking run. PR-tracking agents have no STORY# row; their
 * launch storyId encodes the PR + round:
 *   pr-fix-<pr>-r<n>            (FORGE-302)
 *   pr-resolution-proposer-<pr>-r<n> / pr-resolution-review-<pr>-r<n> (FORGE-309)
 * Shared by the backend (to attach sessions to a PRWatchInfo) and the web
 * (to exclude them from the "ungrouped" table so they aren't shown twice).
 */
export function prNumberFromSessionStoryId(storyId: string): number | null {
  const m = /^pr-(?:fix|resolution-proposer|resolution-review)-(\d+)-r\d+$/.exec(storyId);
  return m ? Number(m[1]) : null;
}

/**
 * FORGE-309 — response for the human's one-click Approve/Reject on a
 * RESOLUTION_READY PR. `action` echoes what was applied; `nextState` is the
 * PRWATCH row's resulting state (WATCHING on approve, PR_FIX_ESCALATED on
 * reject or a non-idempotent no-op).
 */
export interface PostPRResolutionResponse {
  data: {
    prNumber: number;
    repo: string | null;
    action: 'approved_code_merged' | 'approved_comment' | 'rejected';
    nextState: PRWatchState;
    /** GitHub comment/merge URL when one was posted, else null. */
    githubUrl: string | null;
  };
}

/**
 * Session row shape used by the epic detail page. Slimmer than the
 * /sessions/today response because everything here is already scoped
 * to a single epic.
 */
export interface EpicSession {
  /** Stable React key — `<PK>|<SK>`. */
  sessionId: string;
  /** ECS task ARN, useful for opening CloudWatch logs. May be empty. */
  taskArn: string | null;
  /** Story partition this session belongs to. Free-form Forge id. */
  storyId: string;
  agentType: string;
  modelId: string;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheCreateTokens: number;
  iterations: number;
  productiveIterations: number;
  outcome: string | null;
  isRunning: boolean;
  /** FORGE-365: synthesized from a live heartbeat (no SESSION# row until the run ends). */
  live?: boolean;
}

export interface ListEpicsResponse {
  data: {
    epics: EpicSummary[];
    nextCursor: string | null;
  };
}

export interface ErrorResponse {
  error: {
    code: string;
    message: string;
  };
}

export interface StoryDynamoItem {
  PK: string;
  SK: string;
  story_id?: number | string;
  id?: number;
  title?: string;
  state?: string;
  scope?: string[];
  created_at?: string;
  updated_at?: string;
  epic_planner_cost_usd?: number;
  // Forge aggregated cost snapshots — written by orchestrator.ts.
  backend_cost_usd?: number;
  web_cost_usd?: number;
  qa_cost_usd?: number;
  devops_cost_usd?: number;
  lifetime_cost_usd?: number;
  /** Canonical per-story cost rollup written by the orchestrator. When
   *  present, preferred over summing the per-agent fields above. */
  total_cost_usd?: number;
  attempt_count_backend?: number;
  attempt_count_web?: number;
  attempt_count_qa?: number;
  attempt_count_devops?: number;
  /** FORGE-055 — Fibonacci complexity estimate copied onto the STORY
   *  row at /proceed time. Canonical post-approval source. */
  story_points?: number;
  /** GitHub issue number for this story's sub-issue, when the orchestrator
   *  created one. Used by the story-level actions endpoint. */
  github_issue_id?: number;
  // ── FORGE-206 live heartbeat (written by agent run.js while a run is in flight) ──
  agent_run_phase?: string;
  agent_active_type?: string;
  agent_iter?: number;
  agent_max_iter?: number;
  agent_cost_usd_sofar?: number;
  agent_last_tool?: string | null;
  agent_heartbeat_at?: string;
  // ── FORGE-207 resolved-config snapshot + failure analysis ──
  resolved_config?: ResolvedConfigDynamo;
  last_attempt_force_reason?: string;
  last_attempt_notes?: string;
  last_attempt_at?: string;
  stall_reason?: string;
  retry_advice?: RetryAdviceDynamo;
  // ── FORGE-275 characterization-test safety net (written by qa-validate) ──
  /** Per-module coverage, keyed by module path. Inner keys are snake_case. */
  safety_net_module_coverage?: Record<string, ModuleCoverageDynamo>;
  /** Minimum coverage ratio each module must clear (e.g. 0.6). */
  safety_net_coverage_floor?: number;
  /** Module paths below the floor — only on failures. */
  safety_net_below_floor?: string[];
  /** Modules whose coverage regressed against baseline — only on regression failures. */
  safety_net_regressions?: ModuleRegressionDynamo[];
  // ── FORGE-276 legacy .NET migration (written on migration-epic STORY rows) ──
  /** Per-project migratability: 'mechanical' | 'assisted' | 'human-required'. */
  classification?: string;
  /** Migration difficulty in [0, 1] the migration-analyst scored. */
  difficulty?: number;
  /** The project being migrated, e.g. "Acme.Core". */
  migration_project?: string;
  /** Did the new-runtime behaviour match the Framework baseline (parity check). */
  migration_parity?: boolean;
}

/** Raw DDB shape for one module's coverage (FORGE-275). Snake-case. */
export interface ModuleCoverageDynamo {
  coverage?: number;
  lines_covered?: number;
  lines_total?: number;
  files?: number;
}

/** Raw DDB shape for one module regression (FORGE-275). Snake-case. */
export interface ModuleRegressionDynamo {
  module?: string;
  baseline?: number;
  current?: number;
  delta?: number;
}

/** Raw DDB shape written by agent run.js (FORGE-207). Snake-case. */
export interface ResolvedConfigDynamo {
  plan?: string | null;
  tier?: string | null;
  stack_backend?: string | null;
  stack_web?: string | null;
  stack_mobile?: string | null;
  stack_windows?: string | null;
  stack_windows_service?: string | null;
  stack_database?: string | null;
  max_iterations?: number | null;
  max_concurrent_stories?: number | null;
  repo_skills_enabled?: boolean | null;
  mcp_server_count?: number | null;
  snapshot_at?: string | null;
  snapshot_by?: string | null;
}

/**
 * Raw DDB shape of a PRWATCH#<pr-number> row (FORGE-302), written by Forge's
 * orchestrator/webhook/timeout-monitor Lambdas under the epic's own PK.
 * Snake-case, mirrors src/orchestrator/shared/prWatch.ts's PRWatchRow.
 */
export interface PRWatchDynamoItem {
  PK: string;
  SK: string; // PRWATCH#<pr-number> (single-repo) | PRWATCH#<org>/<repo>#<pr> (cross-repo, FORGE-308)
  /** "org/repo" — present on cross-repo epic PR rows (FORGE-308). */
  repo?: string;
  state?: string;
  pr_number?: number;
  pr_url?: string;
  pr_title?: string;
  fix_round?: number;
  queued_batch?: unknown[];
  active_batch?: unknown[];
  last_activity_at?: string;
  last_dispatch_at?: string | null;
  classifier_cost_usd?: number;
  escalation_reason?: string | null;
  config?: { autoFix?: boolean; maxFixRounds?: number };
  head_branch?: string;
  base_branch?: string;
  /** FORGE-309: which propose→review round produced the current proposal (0-based). */
  resolution_round?: number;
  /** FORGE-309: the reviewed recommendation drafted for an escalated PR. */
  proposed_resolution?: {
    summary?: string;
    recommendation?: string;
    hasCodeChanges?: boolean;
    proposalBranch?: string | null;
    reviewVerdict?: 'approve' | 'changes_requested' | null;
    reviewNotes?: string | null;
    humanDecision?: 'approved' | 'rejected' | null;
    decidedBy?: string | null;
  } | null;
}

/**
 * Manual "check for feedback now" trigger (operator-initiated). Fetches the
 * PR's current reviews/comments from GitHub directly instead of waiting on a
 * webhook, seeds a PRWATCH# row if the epic doesn't have one yet (e.g. the PR
 * predates pr_tracking being enabled), and backdates the row so Forge's
 * existing debounce sweep (timeout-monitor.ts, runs every ~5 min) picks the
 * batch up on its very next tick instead of waiting out the normal debounce
 * window. Reuses Forge's existing classify+dispatch pipeline unchanged —
 * this only ever queues raw feedback, it never classifies or dispatches
 * anything itself.
 */
export interface PostPRReviewRefreshResponse {
  data: {
    prNumber: number;
    prUrl: string;
    /** True the first time this endpoint runs for a PR opened before tracking existed. */
    watchRowCreated: boolean;
    /** New items found this call and queued (already-processed ids are skipped). */
    newItemsQueued: number;
    /** Total items now sitting in the row's queued_batch, including any from prior calls/webhooks. */
    totalQueued: number;
    /** PR already merged/closed — nothing was queued. For a cross-repo epic,
     *  true only when EVERY tracked PR is closed. */
    prClosed: boolean;
    /** Rough ETA for when Forge's next debounce sweep will pick this up. */
    nextSweepEtaSeconds: number;
    /** FORGE-308: per-PR breakdown when the epic is cross-repo (one entry per
     *  involved repo's PR). Omitted/single-element for single-repo epics; the
     *  top-level fields above are the aggregate across all entries. */
    perPr?: Array<{
      repo: string | null;
      prNumber: number;
      prUrl: string;
      watchRowCreated: boolean;
      newItemsQueued: number;
      totalQueued: number;
      prClosed: boolean;
    }>;
  };
}

/** Raw DDB shape written by the retry-analyst (FORGE-093). Snake-case. */
export interface RetryAdviceDynamo {
  verdict?: string;
  diagnosis?: string;
  root_cause_kind?: string;
  fix_steps?: string[];
  keep?: string[];
  dont_repeat?: string[];
  produced_at?: string;
}

/**
 * FORGE-206 — live in-flight agent snapshot derived from the STORY row's
 * heartbeat fields. Present on a StorySummary ONLY while an agent is actually
 * running (phase !== 'done' and a heartbeat exists). `stale` is true when the
 * heartbeat has gone cold past the threshold — the task likely died without
 * writing a terminal state (surfaces "stuck" without a CloudWatch dive).
 */
export interface LiveAgent {
  agentType: string;
  phase: string;
  iter: number | null;
  maxIter: number | null;
  costUsd: number | null;
  lastTool: string | null;
  heartbeatAt: string;
  /** Seconds since the last heartbeat, at response time. */
  ageSeconds: number;
  stale: boolean;
  /** FORGE-365: when this run was launched (ISO) — drives the pipeline's live bar. */
  startedAt?: string;
  /**
   * FORGE-363: set while the task is still BOOTING (launched, no agent
   * heartbeat yet) — e.g. a Windows Fargate instance pulling its 8–12 GB image.
   * `stale` then means "didn't start within the expected window".
   */
  starting?: {
    platform: 'windows' | 'linux';
    /** When the launch was requested (ISO). */
    since: string;
    /** Typical boot time for this platform, minutes. */
    expectedMinutes: number;
    /** After this the boot counts as overdue (ISO), or null. */
    overdueAt: string | null;
  };
}

/**
 * A currently-running agent with its epic/story context — powers the Today
 * view's "Running now" section. A LiveAgent plus enough to link back to where
 * it's running. Epic-level agents (planner/PM/analyst) have storyId = null.
 */
export interface LiveAgentActivity extends LiveAgent {
  /** URL-safe epic handle (PK|SK) — same as EpicSummary.epicRef. */
  epicRef: string;
  epicNumber: number;
  repo: string;
  epicTitle: string | null;
  /** Story id when the agent runs against a story; null for epic-level agents. */
  storyId: string | null;
}

/**
 * A single row from Forge's GitHub-event activity log
 * (REPO#<org>/<repo>, SK=GHEVENT#<iso-ts>#<rand>) — added 2026-07-16 so
 * WebPortal can show "yes, we got your comment" and, if something went
 * wrong (repo not attached to a project, unhandled event, an error),
 * surface why, instead of the only trace being CloudWatch logs.
 */
export interface GitHubEventItem {
  eventType: string;
  action: string | null;
  actor: string | null;
  issueNumber: number | null;
  issueUrl: string | null;
  /** First 200 chars only — this is an activity log, not a transcript. */
  commentPreview: string | null;
  outcome: 'ok' | 'dropped_unattached' | 'ignored_unhandled' | 'error';
  reason: string | null;
  createdAt: string;
}

export interface ListGitHubEventsResponse {
  data: {
    events: GitHubEventItem[];
    nextCursor: string | null;
  };
}

export interface StorySummary {
  /**
   * Stable identifier — string because Forge story_ids are sometimes
   * numeric ('0', '1') and sometimes named ('plan', 'graph', 'epic').
   */
  id: string;
  /** Numeric id when it parses, else null. Kept for backwards-compat. */
  numericId: number | null;
  title: string;
  state: string;
  createdAt: string;
  updatedAt: string;
  /** Sum of all per-agent costs the orchestrator has written. */
  lifetimeCostUsd: number;
  /** Number of sessions Forge has run against this story. */
  sessionCount: number;
  // ── FORGE-055 per-story breakdown (FR-5) ──
  /** Fibonacci estimate from the STORY# row; null on legacy stories. */
  storyPoints: number | null;
  /** lifetimeCostUsd / storyPoints; null when storyPoints is null/zero. */
  costPerStoryPoint: number | null;
  /** max(attempt_count_<agent> - 1, 0) summed across agents on this story. */
  retries: number;
  /** Raw SK for diagnostics. */
  sk?: string;
  /** GitHub issue number for this story's sub-issue, when present.
   *  Used by the story-level actions endpoint to post comments. */
  githubIssueNumber?: number;
  /** FORGE-206 — live in-flight agent, present only while a run is active.
   *  Deprecated by `liveAgents`; kept as the first entry for back-compat. */
  liveAgent?: LiveAgent | null;
  /** FORGE-214 — ALL live agents for this story (phase-3 runs several in
   *  parallel). Empty when nothing is running. */
  liveAgents?: LiveAgent[];
  /** FORGE-207 — "why it stopped": exit reason + retry-analyst brief. Present
   *  only when the story row carries failure/force-complete data. */
  failureAnalysis?: StoryFailureAnalysis | null;
  // ── FORGE-275 characterization-test safety net ──
  /** Per-module coverage keyed by module path. Present only on safety-net runs. */
  safetyNetModuleCoverage?: Record<string, ModuleCoverage>;
  /** The coverage floor each module must clear (e.g. 0.6). */
  safetyNetCoverageFloor?: number;
  /** Module paths under the floor — present only on failures. */
  safetyNetBelowFloor?: string[];
  /** Modules that regressed against baseline — present only on regression failures. */
  safetyNetRegressions?: ModuleRegression[];
  // ── FORGE-276 legacy .NET migration ──
  /** Per-project migratability. Present only on migration-epic stories. */
  migrationClassification?: MigrationClassification;
  /** Migration difficulty in [0, 1]. Present only on migration-epic stories. */
  migrationDifficulty?: number;
  /** The project being migrated, e.g. "Acme.Core". */
  migrationProject?: string;
  /** Did new-runtime behaviour match the Framework baseline. Present only when checked. */
  migrationParity?: boolean;
}

// ── FORGE-181: per-story spec / PDR review (GET /api/v1/epics/:id/plan) ──
// The full plan the epic-planner produced — epic overview plus, per story, the
// 4-section body (Description / Acceptance Criteria / Non-Goals / Impl notes).
// Sourced from the plan.json the orchestrator persists to S3 at /proceed.

export interface StorySpec {
  /** Zero-based story index. */
  idx: number;
  /** org/repo this story targets (per story for cross-repo; the one repo otherwise). */
  targetRepo: string | null;
  title: string;
  /** Full 4-section markdown body (Description / Acceptance Criteria / …). */
  body: string;
  scope: string[];
  dependsOn: string[];
  size: 'small' | 'medium' | 'large' | null;
  storyPoints: number | null;
}

export interface EpicPlanView {
  epicNumber: number;
  epicTitle: string;
  /** The epic issue overview/requirements (may be empty). */
  epicBody: string;
  mode: 'single' | 'cross';
  stories: StorySpec[];
  /** ISO timestamp the plan.json was generated, when present. */
  generatedAt: string | null;
}

// Agent session types for GET /api/v1/epics/:id/agent-activity

export interface AgentSessionDynamoItem {
  PK: string;
  SK: string;
  agent_type?: string;
  story_id?: number;
  timestamp?: string;
  model?: string;
  tokens_in?: number;
  tokens_out?: number;
  cost_usd?: number;
  duration_seconds?: number;
  outcome?: string;
}

export interface AgentSession {
  agentType: string;
  storyId: number;
  timestamp: string;
  model: string;
  tokensIn: number;
  tokensOut: number;
  costUsd: number;
  durationSeconds: number;
  outcome: string;
}

export interface AgentActivityResponse {
  data: {
    sessions: AgentSession[];
    nextCursor: string | null;
  };
}

// ---------------------------------------------------------------------------
// Agent transcript (S3 checkpoint JSON)
// ---------------------------------------------------------------------------

/** One content block inside a message — text, tool call, or tool result. */
export type TranscriptContentBlock =
  | { type: 'text'; text: string }
  | { type: 'tool_use'; id: string; name: string; input: unknown }
  | { type: 'tool_result'; tool_use_id: string; content: unknown; is_error?: boolean }
  | { type: 'thinking'; thinking: string }
  | { type: 'image'; source: { type: string; media_type?: string; data?: string } };

export interface TranscriptMessage {
  role: 'user' | 'assistant';
  /** String for plain text, array for content-block messages. */
  content: string | TranscriptContentBlock[];
}

export interface TranscriptView {
  storyId: string;
  agentType: string;
  /** S3 key the transcript was loaded from. */
  s3Key: string;
  iteration: number;
  reason: string | null;
  messages: TranscriptMessage[];
  /** Byte size of the raw checkpoint, for the operator. */
  bytes: number;
}

export interface GetTranscriptResponse {
  data: TranscriptView;
}

// ---------------------------------------------------------------------------
// Cost rollup (FR-7)
// ---------------------------------------------------------------------------

export interface CostBucket {
  count: number;
  costUsd: number;
}

export interface CostSummaryResponse {
  data: {
    fullName: string;
    epics: CostBucket;
    bugfixes: CostBucket;
    total: CostBucket;
  };
}

// ---------------------------------------------------------------------------
// Operator actions (FR-8) — abort / retry via GitHub issue comment
// ---------------------------------------------------------------------------

export type EpicAction = 'abort' | 'retry' | 'proceed' | 'restart';

// ---------------------------------------------------------------------------
// Per-story operator actions — posted to the story's GitHub issue.
// Maps to the orchestrator's story-issue command set:
//   retry   → /retry   (re-launch most recent agent / kick-start from LAUNCHABLE)
//   restart → /restart (re-run the full story chain from the top)
//   abort   → /abort   (kill this story; others continue)
//   unblock → /unblock (clear BLOCKED state)
// ---------------------------------------------------------------------------

export type StoryAction = 'retry' | 'restart' | 'abort' | 'unblock';

export interface PostStoryActionRequest {
  action: StoryAction;
  /** Story id — passed in the body to avoid URL-encoding issues with ids
   *  that contain slashes or hashes (e.g. "SecureNX/dataplus#0").
   *  Optional here; the postStoryAction wrapper injects it from its storyId
   *  parameter. The backend Zod schema enforces it as required. */
  storyId?: string;
  /** Optional free-text note appended to the comment. */
  note?: string;
}

export interface PostStoryActionResponse {
  data: {
    /** The exact GitHub issue comment that was posted. */
    body: string;
    /** GitHub comment URL for the success toast. */
    commentUrl: string;
  };
}

export interface PostEpicActionRequest {
  action: EpicAction;
  /** Optional free-text note appended to the comment. */
  note?: string;
}

export interface PostEpicActionResponse {
  data: {
    /** The exact GitHub issue/PR comment that was posted. */
    body: string;
    /** GitHub comment URL for the success toast. */
    commentUrl: string;
  };
}

// ---------------------------------------------------------------------------
// Plan manifest (FR-1 / FR-6) — pre-/proceed story-point preview
// ---------------------------------------------------------------------------

/**
 * One story as described in the planner's `forge-plan-manifest` block,
 * posted as a GitHub issue comment before the operator clicks /proceed.
 * This is the *pre-approval* SP source; once /proceed runs, the STORY#
 * DDB rows become canonical (see StorySummary.storyPoints).
 */
export interface PlanManifestStory {
  index: number;
  title: string;
  agents: string[];
  /** Planner t-shirt size — "small" | "medium" | "large" (free-form). */
  size: string | null;
  /** Fibonacci complexity estimate; null if the planner omitted it. */
  storyPoints: number | null;
  endpointCount: number | null;
  /** Indices of stories this one depends on. */
  dependsOn: number[];
}

export interface PlanManifest {
  found: boolean;
  stories: PlanManifestStory[];
  /** Sum of storyPoints across stories (skips nulls). */
  totalStoryPoints: number | null;
}

export interface PlanManifestResponse {
  data: PlanManifest;
}

// ---------------------------------------------------------------------------
// Create epic (operator → GitHub issue → Forge picks up via webhook)
// ---------------------------------------------------------------------------

/**
 * Kind discriminator for the create-epic form. Maps to Forge's standard
 * label-based trigger model:
 *   epic       → labels: 'epic' + 'agent-ready'
 *   bugfix     → labels: 'agent:bug'
 *   cross-repo → labels: 'epic-cross-repo' + 'agent-ready' (issue lives
 *                on the anchor repo; body embeds involved_repos manifest)
 */
export type CreateEpicKind = 'epic' | 'bugfix' | 'cross-repo';

export interface CreateEpicRequest {
  kind: CreateEpicKind;
  /** Target repo for epic/bugfix; anchor repo for cross-repo. "org/repo". */
  repo: string;
  /** Cross-repo only: every repo in scope (must include `repo`). */
  involvedRepos?: string[];
  title: string;
  body: string;
  /**
   * When true, the orchestrator skips the /proceed gate and starts
   * building immediately after planning. Default false. Ignored for
   * bugfix (which has no planning step).
   */
  autoApprove?: boolean;
}

export interface CreateEpicResponse {
  data: {
    issueNumber: number;
    issueUrl: string;
    /** Convenience hint of what the epic's DDB PK will be (single-repo only). */
    expectedEpicPk: string | null;
  };
}

// ---------------------------------------------------------------------------
// Epic reference codec (single-repo + cross-repo)
//
// An epic is uniquely identified by its DDB (PK, epic-row SK):
//   single-repo : PK = REPO#<org>/<repo>#EPIC#<n>, SK = META
//   cross-repo  : PK = PROJECT#<id>#EPIC#<n>,      SK = META
//
// `encodeEpicRef` packs that into a URL-safe token used in the detail
// link; `decodeEpicRef` unpacks it on the backend. Pure + isomorphic
// (btoa/atob + TextEncoder/TextDecoder are global in browsers and Node 18+).
// ---------------------------------------------------------------------------

function toBase64Url(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(ref: string): Uint8Array {
  const b64 = ref.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function encodeEpicRef(pk: string, sk: string): string {
  return toBase64Url(new TextEncoder().encode(`${pk}|${sk}`));
}

export function decodeEpicRef(ref: string): { pk: string; sk: string } | null {
  try {
    const raw = new TextDecoder().decode(fromBase64Url(ref));
    const idx = raw.indexOf('|');
    if (idx <= 0 || idx === raw.length - 1) return null;
    return { pk: raw.slice(0, idx), sk: raw.slice(idx + 1) };
  } catch {
    return null;
  }
}

/** Classify a (PK, SK) pair as a single-repo / cross-repo epic row, or null. */
export function epicModelFromKey(pk: string, sk: string): EpicModel | null {
  if (pk.startsWith('PROJECT#') && pk.includes('#EPIC#') && sk === 'META') return 'cross-repo';
  if (pk.startsWith('REPO#') && pk.includes('#EPIC#') && sk === 'META') return 'single-repo';
  return null;
}

/** Parse the epic number from the PK (both single-repo and cross-repo v2 embed it there). */
export function epicNumberFromKey(pk: string, _sk: string): number {
  const m = pk.match(/#EPIC#(\d+)$/);
  return m ? Number(m[1]) : 0;
}

/**
 * Given a SESSION row's (PK, SK), return the (PK, SK) of the epic row it
 * belongs to — so the Today view can build a correct detail link.
 * Both single-repo (REPO#…#EPIC#<n>) and cross-repo v2 (PROJECT#…#EPIC#<n>)
 * own their session rows in the same partition; the epic row is always SK=META.
 * Returns null when the epic can't be identified.
 */
export function epicKeyForSession(pk: string, _sk: string): { pk: string; sk: string } | null {
  if (pk.includes('#EPIC#')) return { pk, sk: 'META' };
  return null;
}
